import Decimal from 'decimal.js'
import { present } from '../../../../lib/util/present'
import { tryParseDecimal } from '../../../../lib/util/tryParseDecimal'

/**
 * Which expenses an advance request may include.
 *
 * An expense already sent to a ministry (or reimbursed by the HRA) is not
 * part of a new advance: the request is for costs that have not been
 * submitted yet. Those expenses stay out of the pre-selected set and out of
 * "Total expense amount".
 */

const HRA_SUBMISSION = 'HRA'

export interface AdvanceExpenseRef {
  id: string
  paidAmount?: string | null
  submitted_at?: string | null
}

export interface AdvanceSubmissionRef {
  submitted_at?: string | null
  submission_type?: string | null
  expense_ids?: string[] | null
}

export interface AdvanceRequestSelection {
  includedExpenseIds: string[]
  submittedExpenseIds: string[]
  hraSubmittedExpenseIds: string[]
  totalExpenseAmount: Decimal
}

export function advanceRequestSelection(input: {
  expenses: AdvanceExpenseRef[]
  submissions: AdvanceSubmissionRef[]
  alreadyAdvancedExpenseIds: string[]
}): AdvanceRequestSelection {
  const submittedSubmissions = input.submissions.filter((submission) => present(submission.submitted_at))
  const hraSubmittedExpenseIds = submittedSubmissions
    .filter((submission) => submission.submission_type === HRA_SUBMISSION)
    .flatMap((submission) => submission.expense_ids ?? [])
  const onASubmittedSubmission = new Set(
    submittedSubmissions.flatMap((submission) => submission.expense_ids ?? []),
  )

  const submittedExpenseIds = input.expenses
    .filter((expense) => present(expense.submitted_at) || onASubmittedSubmission.has(expense.id))
    .map((expense) => expense.id)
  const submitted = new Set(submittedExpenseIds)
  const alreadyAdvanced = new Set(input.alreadyAdvancedExpenseIds)

  const includedExpenseIds = input.expenses
    .filter((expense) => !submitted.has(expense.id) && !alreadyAdvanced.has(expense.id))
    .map((expense) => expense.id)

  const totalExpenseAmount = input.expenses
    .filter((expense) => !submitted.has(expense.id))
    .reduce((total, expense) => total.plus(tryParseDecimal(expense.paidAmount) || new Decimal(0)), new Decimal(0))

  return {
    includedExpenseIds,
    submittedExpenseIds,
    hraSubmittedExpenseIds,
    totalExpenseAmount,
  }
}
